import type { HTMLAttributes } from 'react';

export type BadgeTone = 'neutral' | 'accent' | 'secondary' | 'success' | 'warning' | 'danger';

// The hairlines on `neutral` and `secondary` are not decoration: both fills are
// within a few RGB points of surfaces the badge sits on — the row hover state
// and the green attention tint respectively — so without a border the pill
// dissolved into its own background exactly when the user was pointing at it.
export const BADGE_TONES: Record<BadgeTone, string> = {
  // `--color-border` was only 1.1:1 against the row hover fill — present in the
  // markup, invisible on screen. The muted text token at 40% holds the shape.
  neutral:
    'bg-[var(--color-bg-muted)] text-[var(--color-text-secondary)] border border-[var(--color-text-muted)]/40',
  accent: 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]',
  secondary:
    'bg-[var(--color-secondary-subtle)] text-[var(--color-secondary-text)] border border-[var(--color-secondary)]/30',
  success: 'bg-[var(--color-success-subtle)] text-[var(--color-success)]',
  warning: 'bg-[var(--color-warning-subtle)] text-[var(--color-warning)]',
  danger: 'bg-[var(--color-danger-subtle)] text-[var(--color-danger)]',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
}

/**
 * Small status pill.
 */
export function Badge({ tone = 'neutral', className = '', ...rest }: BadgeProps) {
  return (
    <span
      className={[
        'inline-flex items-center gap-x-1.5 py-1 px-2.5 rounded-full text-xs font-medium',
        BADGE_TONES[tone],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );
}
